import { Routes, Route, Link } from 'react-router-dom'
import { useEffect, useRef, useCallback } from 'react'
import { useAppDispatch, useAppSelector } from './hooks/useAppDispatch'
import { setAccessToken, setUser, setAuthLoading, setRetrying, logout } from './store/slices/authSlice'
import { api } from './lib/apiClient'
import { ENDPOINTS } from './lib/endpoints'
import { STORAGE_KEYS, DOM_EVENTS, TOKEN_REFRESH_INTERVAL_MS, AUTH_MAX_RETRIES, AUTH_MAX_RETRY_DELAY_MS, AUTH_RETRY_BASE_DELAY_MS, ENABLE_DMS, ENABLE_SUPPORT } from './lib/config'
import TopNav from './components/layout/TopNav'
import LeftSidebar from './components/layout/LeftSidebar'
import RightSidebar from './components/layout/RightSidebar'
import HomePage from './pages/HomePage'
import OnboardingModal from './features/onboarding/OnboardingModal'
import WeeklySummaryBanner from './features/weekly/WeeklySummaryBanner'

import AuthCallback from './features/auth/AuthCallback'
import DiscussionDetailPage from './pages/DiscussionDetailPage'
import CreateDiscussionPage from './pages/CreateDiscussionPage'
import ProfilePage from './pages/ProfilePage'
import SettingsPage from './pages/SettingsPage'
import AdminPage from './pages/AdminPage'
import SearchPage from './pages/SearchPage'
import NotFoundPage from './pages/NotFoundPage'
import FollowingPage from './pages/FollowingPage'
import ChatPage from './pages/ChatPage'
import LeaderboardPage from './pages/LeaderboardPage'
import DeveloperPage from './pages/DeveloperPage'
import BillingPage from './pages/BillingPage'
import SupportPage from './pages/SupportPage'

// Shared by the route below and the mount-time session check, so the guard
// can't drift out of sync with the path it's guarding.
const AUTH_CALLBACK_PATH = '/auth/callback'

const Spinner = () => (
  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh', color: 'var(--text-muted)', fontSize: '0.875rem', gap: '0.5rem' }}>
    <div style={{ width: '16px', height: '16px', border: '2px solid var(--border)', borderTopColor: 'var(--accent-primary)', borderRadius: '50%', animation: 'spin 0.6s linear infinite' }} />
    <style>{`@keyframes spin { to { transform: rotate(360deg) } }`}</style>
  </div>
)

const MainLayout = ({ children }: { children: React.ReactNode }) => (
  <div className="main-layout">
    <div className="main-content-wrapper">
      <div className="sidebar-left"><LeftSidebar /></div>
      <main className="main-content">{children}</main>
      <div className="sidebar-right"><RightSidebar /></div>
    </div>
  </div>
)

const FullLayout = ({ children }: { children: React.ReactNode }) => (
  <div className="main-layout">
    <div className="main-content-wrapper">
      <main className="full-layout-main">{children}</main>
    </div>
  </div>
)

function App() {
  const dispatch = useAppDispatch()
  const { isAuthenticated, accessToken } = useAppSelector((s) => s.auth)
  const refreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const isRefreshingRef = useRef(false)
  const doRefreshRef = useRef<(retryCount?: number) => Promise<void>>(async () => {})

  const scheduleRefresh = useCallback((delay = TOKEN_REFRESH_INTERVAL_MS) => {
    if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current)
    refreshTimerRef.current = setTimeout(() => void doRefreshRef.current(), delay)
  }, [])

  const scheduleRetry = useCallback((retryCount: number) => {
    if (retryCount >= AUTH_MAX_RETRIES) {
      dispatch(setRetrying(false))
      dispatch(setAuthLoading(false))
      return
    }
    dispatch(setRetrying(true))
    const delay = Math.min(AUTH_RETRY_BASE_DELAY_MS * Math.pow(2, retryCount), AUTH_MAX_RETRY_DELAY_MS)
    scheduleRefresh(delay)
  }, [scheduleRefresh])

  const doRefresh = useCallback(async (retryCount = 0) => {
    if (isRefreshingRef.current) return
    isRefreshingRef.current = true
    try {
      // Get stored refresh token for cross-domain header-based auth
      const storedRefreshToken = (() => {
        try { return localStorage.getItem(STORAGE_KEYS.refreshToken) } catch { return null }
      })()

      const data = await api.post<{
        accessToken: string
        refreshToken?: string
        user: { id: string; email: string; displayName: string; avatarUrl: string | null; roles: string[] }
      }>('auth', ENDPOINTS.auth.refresh, undefined, undefined, storedRefreshToken ?? undefined)

      // Store new refresh token if returned
      if (data.refreshToken) {
        try { localStorage.setItem(STORAGE_KEYS.refreshToken, data.refreshToken) } catch {}
      }

      dispatch(setRetrying(false))
      dispatch(setAccessToken(data.accessToken))
      dispatch(setUser(data.user))
      scheduleRefresh()
    } catch (err: any) {
      if (err?.status === 401) {
        dispatch(setAuthLoading(false))
        dispatch(setRetrying(false))
        dispatch(logout())
      } else {
        scheduleRetry(retryCount)
      }
    } finally {
      isRefreshingRef.current = false
    }
  }, [scheduleRefresh, scheduleRetry])

  // Keep the ref up-to-date so scheduleRefresh always calls the latest doRefresh
  useEffect(() => { doRefreshRef.current = doRefresh }, [doRefresh])

  // Apply saved theme on mount — DB takes priority over localStorage once logged in
  useEffect(() => {
    const saved = localStorage.getItem(STORAGE_KEYS.theme)
    if (saved) document.documentElement.setAttribute('data-theme', saved)
  }, [])

  // When user logs in, fetch their theme preference from DB and apply it
  useEffect(() => {
    if (!isAuthenticated || !accessToken) return
    api.get<{ preferences: { theme: string } }>('users', ENDPOINTS.users.preferences, accessToken)
      .then((d) => {
        const theme = d.preferences?.theme
        if (theme) {
          document.documentElement.setAttribute('data-theme', theme)
          localStorage.setItem(STORAGE_KEYS.theme, theme)
        }
      })
      .catch(() => {})
  }, [isAuthenticated, accessToken])

  // Initial session check on app load
  useEffect(() => {
    if (window.location.pathname !== AUTH_CALLBACK_PATH) void doRefresh()
    return () => {
      if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current)
    }
  }, [])

  return (
    <>
      <TopNav />
      <OnboardingModal />
      <WeeklySummaryBanner />
      <Routes>
        <Route path={AUTH_CALLBACK_PATH} element={<AuthCallback />} />
        <Route path="/" element={<MainLayout><HomePage /></MainLayout>} />
        <Route path="/discussions/:id" element={<MainLayout><DiscussionDetailPage /></MainLayout>} />
        <Route path="/new" element={<MainLayout><CreateDiscussionPage /></MainLayout>} />
        <Route path="/profile" element={<MainLayout><ProfilePage /></MainLayout>} />
        <Route path="/profile/:id" element={<MainLayout><ProfilePage /></MainLayout>} />
        <Route path="/settings" element={<MainLayout><SettingsPage /></MainLayout>} />
        <Route path="/following" element={<MainLayout><FollowingPage /></MainLayout>} />
        {ENABLE_DMS && <Route path="/chat" element={<FullLayout><ChatPage /></FullLayout>} />}
        {ENABLE_DMS && <Route path="/chat/:agentId" element={<FullLayout><ChatPage /></FullLayout>} />}
        <Route path="/admin" element={<MainLayout><AdminPage /></MainLayout>} />
        <Route path="/search" element={<MainLayout><SearchPage /></MainLayout>} />
        <Route path="/leaderboard" element={<MainLayout><LeaderboardPage /></MainLayout>} />
        <Route path="/developer" element={<MainLayout><DeveloperPage /></MainLayout>} />
        <Route path="/billing" element={<MainLayout><BillingPage /></MainLayout>} />
        {ENABLE_SUPPORT && <Route path="/support" element={<MainLayout><SupportPage /></MainLayout>} />}
        <Route path="/welcome" element={
          <MainLayout>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '60vh', color: 'var(--text-primary)', gap: '1rem', textAlign: 'center' }}>
              <div style={{ fontSize: '3rem' }}>🎉</div>
              <h1 style={{ fontSize: '2rem', margin: 0 }}>Welcome to Ekkam AI!</h1>
              <p style={{ color: 'var(--text-muted)', margin: 0 }}>Start your first discussion!</p>
              <Link to="/new" style={{ background: 'var(--accent-primary)', color: 'white', padding: '0.65rem 1.5rem', borderRadius: '8px', textDecoration: 'none', fontWeight: 600 }}>
                ➕ Start a Discussion
              </Link>
            </div>
          </MainLayout>
        } />
        <Route path="*" element={<MainLayout><NotFoundPage /></MainLayout>} />
      </Routes>
    </>
  )
}

export default App
